import { FLUENT_CDN_BASE_URL } from '@fluentui/style-utilities';
import { getFileTypeIconAsUrl } from '@fluentui/react-file-type-icons';

/**
 * Sizes Microsoft's file-type icon CDN actually serves. Picking a size not in
 * this list still resolves (the CDN falls back), but these render sharpest.
 */
type OfficeFileIconSize = 16 | 20 | 24 | 32 | 40 | 48 | 64 | 96;

/** Sizes the M365 product-icon CDN serves; SVGs scale cleanly to any rendered size. */
const PRODUCT_ICON_SIZES = [16, 20, 24, 32, 48] as const;

/**
 * Maps a file extension to the M365 product-icon name for the app that owns
 * it (https://developer.microsoft.com/en-us/fluentui#/styles/web/m365-product-icons).
 * Only extensions with a real Microsoft product icon belong here — everything
 * else falls back to the generic file-type icon set (which also covers PDF,
 * since Acrobat isn't a Microsoft product).
 */
const EXTENSION_TO_PRODUCT: Record<string, string> = {
    doc: 'word',
    docx: 'word',
    docm: 'word',
    dot: 'word',
    dotx: 'word',
    dotm: 'word',
    xls: 'excel',
    xlsx: 'excel',
    xlsm: 'excel',
    xlsb: 'excel',
    xlt: 'excel',
    xltx: 'excel',
    ppt: 'powerpoint',
    pptx: 'powerpoint',
    pptm: 'powerpoint',
    pot: 'powerpoint',
    potx: 'powerpoint',
    pps: 'powerpoint',
    ppsx: 'powerpoint',
    one: 'onenote',
    onetoc2: 'onenote',
    vsd: 'visio',
    vsdx: 'visio',
    vsdm: 'visio',
    vst: 'visio',
    vstx: 'visio',
    mpp: 'project',
    accdb: 'access',
    accde: 'access',
    mdb: 'access',
    pub: 'publisher'
};

function getProductIconUrl(extension: string, size: number): string | null {
    const product = EXTENSION_TO_PRODUCT[extension];
    if (!product) return null;

    const closestSize =
        PRODUCT_ICON_SIZES.find((candidate) => candidate >= size) ??
        PRODUCT_ICON_SIZES[PRODUCT_ICON_SIZES.length - 1];

    return `${FLUENT_CDN_BASE_URL}/assets/brand-icons/product/svg/${product}_${closestSize}x1.svg`;
}

interface OfficeFileIconProps {
    /** File extension, with or without a leading dot (e.g. "pdf" or ".pdf"). */
    extension?: string | null;
    size?: OfficeFileIconSize;
    className?: string;
}

/**
 * Renders the current Microsoft 365 icon for a file extension: the app's own
 * product icon (Word, Excel, PowerPoint, ...) where one exists, falling back
 * to Microsoft's generic file-type icon set otherwise (PDF, images, archives,
 * etc.). Both sets are sourced live from Microsoft's Fluent UI CDN.
 */
export function OfficeFileIcon({
    extension,
    size = 32,
    className
}: OfficeFileIconProps) {
    const ext = (extension || '').replace(/^\./, '').toLowerCase();
    const src =
        (ext && getProductIconUrl(ext, size)) ||
        getFileTypeIconAsUrl({ extension: ext || undefined, size });

    return (
        <img
            src={src}
            alt={ext ? `${ext} file` : 'file'}
            width={size}
            height={size}
            draggable={false}
            className={className}
        />
    );
}
